import { FC, useEffect, useState } from 'react';
import { Palette, RefreshCw } from 'lucide-react';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label as UiLabel } from '@/components/ui/label';
import { useCreateOrgLabel } from '@/hooks/mutations/use-create-org-label';
import { useUpdateOrgLabel } from '@/hooks/mutations/use-update-org-label';
import { useNotification } from '@/components/notifications';
import { Label } from '@/types/workflow-types';
import { cn } from '@/lib/utils';

const DEFAULT_COLOR = '#2A9D90';

const DEFAULT_LABEL_COLORS = [
  '#274754',
  '#2A9D90',
  '#27AE60',
  '#E8C468',
  '#F4A462',
  '#E76E50',
  '#E91E63',
  '#9B59B6',
  '#8FA5AC',
  '#93C9C2',
  '#96D6AE',
  '#F2E0A6',
  '#F7D5B8',
  '#F2B3A3',
  '#F3A8C1',
  '#C9ADDA',
];

const REROLL_POOL = [
  '#2A9D90',
  '#E76E50',
  '#E8C468',
  '#3498DB',
  '#9B59B6',
  '#E91E63',
  '#274754',
  '#F4A462',
  '#27AE60',
  '#1ABC9C',
];

const HEX_RE = /^#?[0-9a-fA-F]{6}$/;

interface CreateLabelModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  initialName?: string;
  editingLabel?: Label | null;
  onCreated?: (label: Label) => void;
}

export const CreateLabelModal: FC<CreateLabelModalProps> = ({
  isOpen,
  onOpenChange,
  initialName,
  editingLabel,
  onCreated,
}) => {
  const isEditing = !!editingLabel;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [hexInput, setHexInput] = useState(DEFAULT_COLOR);
  const [hexValid, setHexValid] = useState(true);
  const createMutation = useCreateOrgLabel();
  const updateMutation = useUpdateOrgLabel();
  const notification = useNotification();
  const isPending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (!isOpen) return;
    const seedColor = (editingLabel?.color || DEFAULT_COLOR).toUpperCase();
    setName(editingLabel?.name ?? initialName?.trim() ?? '');
    setDescription(editingLabel?.description ?? '');
    setColor(seedColor);
    setHexInput(seedColor);
    setHexValid(true);
  }, [isOpen, initialName, editingLabel]);

  const applyColor = (c: string) => {
    setColor(c);
    setHexInput(c.toUpperCase());
    setHexValid(true);
  };

  const handleHexChange = (value: string) => {
    setHexInput(value);
    const trimmed = value.trim();
    const ok = HEX_RE.test(trimmed);
    setHexValid(ok || trimmed === '');
    if (ok) setColor(trimmed[0] === '#' ? trimmed : `#${trimmed}`);
  };

  const reroll = () => {
    const pool = REROLL_POOL.filter(
      (c) => c.toLowerCase() !== color.toLowerCase(),
    );
    applyColor(pool[Math.floor(Math.random() * pool.length)]);
  };

  const canSubmit = name.trim().length > 0 && hexValid && !isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      if (editingLabel) {
        await updateMutation.mutateAsync({
          id: editingLabel.id,
          name: name.trim(),
          description: description.trim() || null,
          color,
        });
      } else {
        const label = await createMutation.mutateAsync({
          name: name.trim(),
          description: description.trim() || undefined,
          color,
        });
        onCreated?.(label);
      }
      onOpenChange(false);
    } catch {
      // The mutations' onError already reports to Sentry; here we only surface
      // the failure to the user.
      notification.error(
        isEditing ? 'Failed to update label' : 'Failed to create label',
      );
    }
  };

  const submitLabel = isEditing
    ? isPending
      ? 'Saving...'
      : 'Save'
    : isPending
      ? 'Creating...'
      : 'Create label';

  return (
    <Modal
      isDialogOpen={isOpen}
      setIsDialogOpen={onOpenChange}
      title={isEditing ? 'Edit label' : 'Create new label'}
      footer={
        <div className='flex items-center justify-end gap-2'>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitLabel}
          </Button>
        </div>
      }
    >
      <div className='flex flex-col gap-5'>
        <div className='flex flex-col gap-2'>
          <span className='text-sm font-medium text-muted-foreground'>
            Preview
          </span>
          <div>
            <span
              className='inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium text-foreground'
              style={{
                borderColor: `${color}66`,
                backgroundColor: `${color}1f`,
              }}
            >
              <span
                className='size-2 rounded-full'
                style={{ backgroundColor: color }}
              />
              {name.trim() || 'label preview'}
            </span>
          </div>
        </div>

        <div className='flex flex-col gap-1.5'>
          <UiLabel htmlFor='new-label-name'>Label name</UiLabel>
          <Input
            id='new-label-name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Label name'
            maxLength={100}
            autoFocus
          />
        </div>

        <div className='flex flex-col gap-1.5'>
          <UiLabel htmlFor='new-label-description'>
            Description{' '}
            <span className='font-normal text-muted-foreground'>Optional</span>
          </UiLabel>
          <Input
            id='new-label-description'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='What this label is for'
            maxLength={500}
          />
        </div>

        <div className='flex flex-col gap-2'>
          <UiLabel>Color</UiLabel>
          <div className='flex items-center gap-2'>
            <label
              className='relative inline-flex size-9 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-md text-white shadow-sm'
              style={{ backgroundColor: color }}
            >
              <Palette className='size-4 opacity-90' />
              <input
                type='color'
                value={color}
                onChange={(e) => applyColor(e.target.value)}
                className='absolute inset-0 cursor-pointer opacity-0'
                aria-label='Pick a custom color'
              />
            </label>
            <Input
              value={hexInput}
              onChange={(e) => handleHexChange(e.target.value)}
              placeholder={DEFAULT_COLOR}
              className={cn(
                'flex-1 font-mono uppercase',
                !hexValid &&
                  'border-destructive focus-visible:ring-destructive',
              )}
            />
            <button
              type='button'
              onClick={reroll}
              aria-label='Random color'
              className='inline-flex size-9 shrink-0 items-center justify-center rounded-md border border-input bg-background text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground'
            >
              <RefreshCw className='size-4' />
            </button>
          </div>
          <p
            className={cn(
              'text-xs leading-snug',
              hexValid ? 'text-muted-foreground' : 'text-destructive',
            )}
          >
            {hexValid
              ? 'Hex colors should only contain numbers and letters from a–f.'
              : 'Invalid hex color.'}
          </p>
          <span className='pt-1 text-xs text-muted-foreground'>
            Choose from default colors:
          </span>
          <div className='grid grid-cols-8 gap-2'>
            {DEFAULT_LABEL_COLORS.map((c) => (
              <button
                key={c}
                type='button'
                aria-label={`Select color ${c}`}
                onClick={() => applyColor(c)}
                className={cn(
                  'size-8 rounded-md transition-transform hover:scale-105',
                  color.toLowerCase() === c.toLowerCase() &&
                    'ring-2 ring-offset-2 ring-offset-background ring-primary',
                )}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};
